import { useEffect, useState } from 'react';
import type { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import { captureAndReportError } from '../../../utils/errorReporting';
import { ArDriveProvider } from '../../../utils/upload-providers';
import { createArDriveWalletAdapter } from '../../../utils/wallet';

export interface UseArDriveBalanceArgs {
  isSelected: boolean;
  wallet: ReturnType<typeof useWallet>;
}

export interface UseArDriveBalanceResult {
  ardriveBalance: string | null;
  checkingArDriveBalance: boolean;
  fileSize: number;
  setFileSize: (value: number) => void;
  estimatedCost: string | null;
  remainingArDriveBalance: string | null;
  arToUsdRate: number | null;
  setArToUsdRate: (value: number | null) => void;
  checkArDriveBalance: () => Promise<void>;
}

export function useArDriveBalance({
  isSelected,
  wallet,
}: UseArDriveBalanceArgs): UseArDriveBalanceResult {
  const [ardriveBalance, setArdriveBalance] = useState<string | null>(null);
  const [checkingArDriveBalance, setCheckingArDriveBalance] = useState(false);
  const [fileSize, setFileSize] = useState(50);
  const [estimatedCost, setEstimatedCost] = useState<string | null>(null);
  const [remainingArDriveBalance, setRemainingArDriveBalance] = useState<
    string | null
  >(null);
  const [arToUsdRate, setArToUsdRate] = useState<number | null>(null);

  const checkArDriveBalance = async () => {
    if (!wallet.publicKey || !wallet.signMessage) {
      return;
    }

    setCheckingArDriveBalance(true);
    try {
      const provider = new ArDriveProvider();
      const walletAdapter = createArDriveWalletAdapter(wallet, {
        throwOnInvalid: false,
      });

      if (!walletAdapter) return;

      await provider.initialize({
        walletAdapter,
      });

      const [{ balance }, { arToUsd }] = await Promise.all([
        provider.getBalance(),
        provider.getFiatRates(),
      ]);

      setArdriveBalance(balance);
      setArToUsdRate(arToUsd);
    } catch (error) {
      captureAndReportError('Failed to check ArDrive balance:', error);
      toast.error(
        'Failed to check balance. Please ensure your wallet is connected.',
      );
    } finally {
      setCheckingArDriveBalance(false);
    }
  };

  const estimateArDriveCost = async () => {
    if (!fileSize) {
      toast.error('Please enter a file size');
      return;
    }

    try {
      const provider = new ArDriveProvider();
      const bytes = fileSize * 1024 * 1024;
      const { cost } = await provider.getUploadCost(bytes);
      setEstimatedCost(cost);
    } catch (error) {
      captureAndReportError('Failed to estimate ArDrive cost:', error);
      toast.error('Failed to estimate cost.');
    }
  };

  useEffect(() => {
    if (isSelected) {
      if (
        !ardriveBalance &&
        !checkingArDriveBalance &&
        wallet.publicKey &&
        !wallet.connecting
      ) {
        const timer = setTimeout(() => {
          checkArDriveBalance();
        }, 100);

        return () => clearTimeout(timer);
      }

      const fetchRate = async () => {
        try {
          const provider = new ArDriveProvider();
          const { arToUsd } = await provider.getFiatRates();
          setArToUsdRate(arToUsd);
        } catch (error) {
          captureAndReportError('Failed to fetch USD rate:', error);
        }
      };
      fetchRate();
    }
  }, [isSelected, wallet.publicKey, wallet.connecting]);

  useEffect(() => {
    if (ardriveBalance && estimatedCost) {
      const balanceFloat = parseFloat(ardriveBalance);
      const costFloat = parseFloat(estimatedCost);
      if (!Number.isNaN(balanceFloat) && !Number.isNaN(costFloat)) {
        const remaining = balanceFloat - costFloat;
        const remainingString = remaining.toFixed(12).replace(/\.?0+$/, '');
        setRemainingArDriveBalance(remainingString);
      }
    } else if (ardriveBalance && !estimatedCost) {
      setRemainingArDriveBalance(ardriveBalance);
    }
  }, [ardriveBalance, estimatedCost]);

  useEffect(() => {
    if (isSelected && fileSize) {
      const timer = setTimeout(() => {
        estimateArDriveCost();
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [fileSize, isSelected]);

  return {
    ardriveBalance,
    checkingArDriveBalance,
    fileSize,
    setFileSize,
    estimatedCost,
    remainingArDriveBalance,
    arToUsdRate,
    setArToUsdRate,
    checkArDriveBalance,
  };
}
